import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import * as generic from '../../../components/Generico';
import { JuridicaService } from '../../../service/JuridicaService';
import { InputComponent } from '../../../components/InputComponent';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { RepresentanteApoderado } from '../RepresentanteApoderado';
import { JuridicaEconomica } from '../../../models/ficha/JuridicaEconomica';
import { BottomActionBar } from '../../../components/ui/BottomActionBar';

export const InformacionEconomica = ({ toast, back, next, id, permss, soloLectura, catalogos }) => {

    const omodel = new JuridicaEconomica({ tipoFrmEmpresa: catalogos?.tipoFrmEmpresa });
    const defaultValues = generic.funDefault(omodel.model);
    const { control, formState: { errors }, handleSubmit, reset, setError, getValues, setValue } = useForm({ defaultValues });
    const juridicaSrv = new JuridicaService();
    const { inNumber } = InputComponent({ omodel, errors, control, permss, id, soloLectura });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        editarFicha();
        // eslint-disable-next-line
    }, [id]);

    const editarFicha = () => {
        setIsLoading(true);
        juridicaSrv.consultarJuridica(id, 'seccion2').then(res => {
            if (!res.data.juridica) {
                return;
            }
            reset(generic.renewValues(res.data.juridica, omodel.model));
        }).finally(() => setIsLoading(false));
    }

    const onSubmit = (data) => {
        setIsSaving(true);
        data.id = id;
        data.seccion = 'seccion2';
        juridicaSrv.editarJuridica(data).then(res => {
            generic.funcSuccess(res, toast);
            next(id);
        }).catch(error => {
            generic.funcError(error, toast, setError);
        }).finally(() => setIsSaving(false));
    }

    const onError = () => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Se detectaron errores, por favor revise el formulario para corregir.', life: 3000 });
    }

    const actualizarPatrimonio = () => {
        const actvs = ['total_activo_corriente', 'total_activo_no_corriente'];
        const pasvs = ['total_pasivo_corriente', 'total_pasivo_no_corriente'];
        let totalPasivos = 0;
        let totalActivos = 0;
        actvs.forEach(itm => {
            if (getValues(itm) && !isNaN(getValues(itm))) {
                totalActivos += parseFloat(getValues(itm));
            }
        });
        pasvs.forEach(itm => {
            if (getValues(itm) && !isNaN(getValues(itm))) {
                totalPasivos += parseFloat(getValues(itm));
            }
        });
        setValue('total_activos', totalActivos);
        setValue('total_pasivos', totalPasivos);
        setValue('patrimonio', totalActivos - totalPasivos);
    }

    if (isLoading) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-5">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
                <span className="mt-3 text-color-secondary font-medium text-lg">Consultando información...</span>
            </div>
        );
    }

    return (
        <div className="p-fluid">
            <Panel header={generic.renderPanelHeader('2.1', 'DATOS DEL REPRESENTANTE LEGAL / APODERADO', 'pi pi-id-card')} className="panel-ficha shadow-1 mb-4">
                <RepresentanteApoderado toast={toast} tipo="juridica" id={id} permss={permss} soloLectura={soloLectura} tipoFrmEmpresa={catalogos?.tipoFrmEmpresa} />
            </Panel>

            <Panel header={generic.renderPanelHeader('2.2', 'INFORMACIÓN ECONÓMICA', 'pi pi-chart-line')} className="panel-ficha shadow-1 mb-4">
                <form id="infEconomicaForm" onSubmit={handleSubmit(onSubmit, onError)}>

                    <Divider align="center" className="mt-1 mb-3">
                        <span className="p-tag p-tag-rounded p-tag-secondary">Ingresos y Egresos Mensuales</span>
                    </Divider>
                    <div className="formgrid grid">
                        {inNumber('ingreso_mensual', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left', func: actualizarPatrimonio }, 6)}
                        {inNumber('egreso_mensual', 2, true, { icon: 'pi pi-arrow-down-right', iconPosition: 'left', func: actualizarPatrimonio }, 6)}
                        {inNumber('ingreso_anio', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left', func: actualizarPatrimonio }, 6)}
                        {inNumber('egreso_anio', 2, true, { icon: 'pi pi-arrow-down-right', iconPosition: 'left', func: actualizarPatrimonio }, 6)}
                    </div>

                    <div className="grid formgrid mt-2">
                        {/* COLUMNA ACTIVOS */}
                        <div className="col-12 md:col-6 mb-2 md:mb-0">
                            <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1">
                                <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">ACTIVOS</div>
                                {inNumber('total_activo_corriente', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('total_activo_no_corriente', 2, true, { icon: 'pi pi-building', iconPosition: 'left', func: actualizarPatrimonio }, 12)}

                                <div className="mt-auto">
                                    <Divider type="dashed" className="my-2" />
                                    <div className="bg-blue-50 p-2 border-round border-1 border-blue-200">
                                        {inNumber('total_activos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true }, 12)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* COLUMNA PASIVOS */}
                        <div className="col-12 md:col-6">
                            <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1">
                                <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">PASIVOS</div>
                                {inNumber('total_pasivo_corriente', 2, true, { icon: 'pi pi-calendar-minus', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('total_pasivo_no_corriente', 2, true, { icon: 'pi pi-calendar-plus', iconPosition: 'left', func: actualizarPatrimonio }, 12)}

                                <div className="mt-auto">
                                    <Divider type="dashed" className="my-2" />
                                    <div className="bg-blue-50 p-2 border-round border-1 border-blue-200">
                                        {inNumber('total_pasivos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true }, 12)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN RESULTADOS (Patrimonio) */}
                    <div className="grid mt-2">
                        <div className="col-12 md:col-4 md:col-offset-4">
                            <div className="bg-blue-100 p-2 border-round border-1 border-blue-300 shadow-2">
                                {inNumber('patrimonio', 2, true, { icon: 'pi pi-wallet', iconPosition: 'left', disabled: true, min: 'nulo' }, 12)}
                            </div>
                        </div>
                    </div>
                </form>
            </Panel>

            <BottomActionBar formId="infEconomicaForm" onPrev={back} onNext={next} loading={isSaving} isReadOnly={soloLectura} />
        </div>
    );
}
